import type { RealtimeEntityPropertyValue } from '@interfaces/realtimeEntities';

export type { RealtimeEntityPropertyValue };

type SendMessageToUnreal = (message: unknown) => void;

export interface RealtimeEntityTransform {
  /** `[X, Y, Z]`, Unreal units. */
  position?: number[];
  /** `[Pitch, Yaw, Roll]`, degrees — FRotator constructor order, matching the hotspot wire format. */
  rotation?: number[];
  /** `[X, Y, Z]` per-axis factors; absent defaults to unit on spawn. */
  scale?: number[];
}

export interface RealtimeEntitySpawnPayload extends RealtimeEntityTransform {
  /** Wire id of the entity — without one the engine never tracks it. */
  entityId: string;
  /** A `typeId` from the build's `spawnableObjects` schema — never a class path. */
  type: string;
  properties?: Record<string, RealtimeEntityPropertyValue>;
}

export interface RealtimeEntityUpdatePayload extends RealtimeEntityTransform {
  entityId: string;
  properties?: Record<string, RealtimeEntityPropertyValue>;
}

/**
 * Keeps an update payload to exactly the fields the caller set. Presence, not
 * value, decides whether the engine applies a field — `position: [0,0,0]`
 * moves an entity to the origin while an absent `position` leaves it alone —
 * so an untouched field must never ride along as `undefined` or a default.
 *
 * @param transform - Candidate transform fields, each possibly undefined.
 * @param properties - Candidate properties object, possibly empty.
 * @returns Only the entries that are actually present (a non-empty properties object included).
 */
const pickPresentFields = (
  transform: RealtimeEntityTransform,
  properties?: Record<string, RealtimeEntityPropertyValue>,
): Record<string, unknown> => {
  return {
    ...(transform.position ? { position: transform.position } : {}),
    ...(transform.rotation ? { rotation: transform.rotation } : {}),
    ...(transform.scale ? { scale: transform.scale } : {}),
    ...(properties && Object.keys(properties).length ? { properties } : {}),
  };
};

/**
 * Reads one value as a finite `[x, y, z]` tuple, or nothing.
 *
 * @param value - Candidate transform field from an engine report.
 */
const parseFiniteTriple = (value: unknown): number[] | undefined => {
  return Array.isArray(value) &&
    value.length === 3 &&
    value.every((component) => Number.isFinite(component))
    ? (value as number[])
    : undefined;
};

/** A validated engine transform report for one tracked entity. */
export interface RealtimeEntityReport {
  entityId: string;
  position?: number[];
  rotation?: number[];
  scale?: number[];
}

/**
 * Validates the engine's UPDATE_REALTIME_ENTITY report before anything stores
 * or renders it. The report crosses a trust boundary — a relayed message with
 * `[null, 0, 0]` or string elements would otherwise crash the inspector's
 * formatters — so only finite 3-element tuples survive, and a scalar `scale`
 * (the uniform-scale shape the hotspot pipeline also sends) is expanded to a
 * per-axis tuple.
 *
 * @param report - The raw inbound message.
 * @returns The validated transform, or `null` when the report carries no
 * entity id or no usable transform field at all.
 */
export const parseRealtimeEntityReport = (
  report: unknown,
): RealtimeEntityReport | null => {
  const raw = report as Record<string, unknown> | null;
  const entityId = typeof raw?.entityId === 'string' ? raw.entityId : '';

  if (!entityId) {
    return null;
  }

  const rawScale = raw?.scale;
  const scale =
    typeof rawScale === 'number' && Number.isFinite(rawScale)
      ? [rawScale, rawScale, rawScale]
      : parseFiniteTriple(rawScale);
  const position = parseFiniteTriple(raw?.position);
  const rotation = parseFiniteTriple(raw?.rotation);

  const transform = {
    ...(position ? { position } : {}),
    ...(rotation ? { rotation } : {}),
    ...(scale ? { scale } : {}),
  };

  if (!Object.keys(transform).length) {
    return null;
  }

  return { entityId, ...transform };
};

/**
 * Spawns one or more real-time entities in the author's engine instance.
 *
 * Every entity is sent with an `entityId`: the engine tracks entities only by
 * the id they were spawned with, so an entity spawned without one could never
 * be edited or deleted afterwards.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entities - Entities to create; absent position/rotation default to
 * origin/zero and absent scale to unit, engine-side.
 */
export const spawnRealtimeEntitiesInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entities: RealtimeEntitySpawnPayload[],
) => {
  sendMessageToUnreal({
    action: 'SPAWN_REALTIME_ENTITIES',
    entities: entities.map((entity) => ({
      entityId: entity.entityId,
      type: entity.type,
      ...pickPresentFields(entity, entity.properties),
    })),
  });
};

/**
 * Moves and/or re-configures one spawned entity. One entity per call — the
 * action is not a batch.
 *
 * A `properties` object writes only the keys it carries, so editing one field
 * cannot reset the rest to defaults.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param update - The entity id plus only the fields that actually changed.
 */
export const updateRealtimeEntityInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  update: RealtimeEntityUpdatePayload,
) => {
  sendMessageToUnreal({
    action: 'UPDATE_REALTIME_ENTITY',
    entityId: update.entityId,
    ...pickPresentFields(update, update.properties),
  });
};

/**
 * Removes one spawned entity. The engine reads `entityId` and nothing else,
 * and refuses an absent or empty id rather than reading it as "delete
 * everything" — this guard mirrors that so a bug here cannot even ask.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entityId - Entity to remove; its id is free for reuse afterwards.
 */
export const deleteRealtimeEntityInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entityId: string,
) => {
  if (!entityId) {
    return;
  }

  sendMessageToUnreal({
    action: 'DELETE_REALTIME_ENTITY',
    entityId,
  });
};

/**
 * Opens the in-world transform gizmo on one spawned entity.
 *
 * Sending a start for a different id while one entity is already being edited
 * switches the gizmo over — no explicit end is required first.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entityId - Entity to attach the gizmo to.
 */
export const startRealtimeEntityEditInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entityId: string,
) => {
  sendMessageToUnreal({
    action: 'START_REALTIME_ENTITY_EDIT',
    entityId,
  });
};

/**
 * Closes the in-world transform gizmo.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entityId - Entity to end the edit for; the engine ends whatever is
 * selected when this is omitted, and ignores an id naming a different entity
 * than the selected one (a stale race).
 */
export const endRealtimeEntityEditInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entityId?: string,
) => {
  sendMessageToUnreal({
    action: 'END_REALTIME_ENTITY_EDIT',
    ...(entityId ? { entityId } : {}),
  });
};
